/**
 * Handles /callback after APS PKCE redirect, then sends the user home.
 */
import { useEffect, useState } from "preact/hooks";
import { ALPHA_SUPPORT_MESSAGE } from "../featureFlags/launchDarklyConfig";
import { useAuthStore } from "../stores/authStore";

/** OAuth errors where silent SSO had no Autodesk session — not user-facing failures. */
function isSilentSsoMiss(errorCode: string | null): boolean {
  return (
    errorCode === "login_required" ||
    errorCode === "interaction_required" ||
    errorCode === "consent_required"
  );
}

export function AuthCallback() {
  const handleOAuthCallback = useAuthStore(s => s.handleOAuthCallback);
  const error = useAuthStore(s => s.error);
  const status = useAuthStore(s => s.status);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error_description") ?? params.get("error");
    const errorCode = params.get("error");

    if (oauthError && isSilentSsoMiss(errorCode)) {
      window.history.replaceState({}, "", "/");
      window.location.assign("/");
      return;
    }

    if (oauthError) {
      setLocalError(oauthError);
      return;
    }
    if (!code) {
      setLocalError("No authorization code in callback URL.");
      return;
    }

    void handleOAuthCallback(code, state)
      .then(() => {
        const denied = useAuthStore.getState().status === "access_denied";
        window.history.replaceState({}, "", "/");
        window.location.assign("/");
        if (denied) return;
      })
      .catch(err => {
        setLocalError(err instanceof Error ? err.message : String(err));
      });
  }, [handleOAuthCallback]);

  const message =
    status === "access_denied" ? ALPHA_SUPPORT_MESSAGE : (localError ?? error);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, sans-serif",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <h1 style={{ fontSize: 18, marginBottom: 8 }}>Vera</h1>
        {message ? (
          <>
            <p style={{ color: "#c0392b" }}>{message}</p>
            <a href="/">Back to Vera</a>
          </>
        ) : (
          <p>Completing sign-in…</p>
        )}
      </div>
    </div>
  );
}
